import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import * as Icons from "lucide-react";
import { Menu, X, LogOut, Shield, Sparkles, LayoutDashboard, Users, ChevronDown, Search, Bell, Radio, Camera, Landmark, TrendingUp, ShoppingBag, Truck, Receipt, Package, Gavel, Factory } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { ROLE_LABELS, ROLE_TIER, TIER_LABEL, TIER_STYLE } from "@/lib/constants";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ops-command-center-87/artifacts/fkrzr67n_4446800E-690F-4C11-82C8-015EFBEC0CAD.png";

function LucideIcon({ name, className }) {
  const Cmp = Icons[name] || Icons.Square;
  return <Cmp className={className} />;
}

export default function AppLayout({ children }) {
  const { user, logout, modulesAccess } = useAuth();
  const [modulesMeta, setModulesMeta] = useState([]);
  const [groups, setGroups] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [alertsCount, setAlertsCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  // One-shot bootstrap on mount — modules-meta & alert-count don't depend on any
  // reactive variable in this scope.
  useEffect(() => {
    api.get("/meta/modules").then((r) => {
      setModulesMeta(r.data.modules || []);
      setGroups(r.data.groups || []);
    });
    api.get("/dashboard/alerts").then((r) => setAlertsCount((r.data.alerts || []).length)).catch(() => {});
  }, []);

  // Only reacts to route change — the closed-over `setMobileOpen` is a stable React setter.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const accessibleModules = useMemo(
    () => modulesMeta.filter((m) => m.has_access),
    [modulesMeta]
  );

  const grouped = useMemo(() => {
    const map = {};
    for (const g of groups) map[g] = [];
    for (const m of accessibleModules) {
      if (!map[m.group]) map[m.group] = [];
      map[m.group].push(m);
    }
    return map;
  }, [accessibleModules, groups]);

  const initials = (user?.name || user?.email || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const isAdmin = user?.role === "admin" || user?.role === "dept_head";
  const roleDashboards = useMemo(() => {
    if (!user) return [];
    const list = [];
    const exec = isAdmin;
    if (exec || ["sales", "ecommerce"].includes(user.role)) list.push({ to: "/sales-dashboard", icon: TrendingUp, label: "Sales Dashboard", color: "text-emerald-400", testid: "sidebar-sales-dashboard-link" });
    if (exec || ["procurement", "procurement_staff"].includes(user.role)) list.push({ to: "/purchase-dashboard", icon: ShoppingBag, label: "Purchase Dashboard", color: "text-amber-400", testid: "sidebar-purchase-dashboard-link" });
    if (exec || ["dispatch", "demo_install"].includes(user.role)) list.push({ to: "/dispatch-dashboard", icon: Truck, label: "Dispatch Dashboard", color: "text-blue-400", testid: "sidebar-dispatch-dashboard-link" });
    if (exec || user.role === "accounts") list.push({ to: "/accounts-dashboard", icon: Receipt, label: "Accounts Dashboard", color: "text-violet-400", testid: "sidebar-accounts-dashboard-link" });
    if (exec || ["tender", "tender_assistant"].includes(user.role)) list.push({ to: "/tender-dashboard", icon: Gavel, label: "Tender Dashboard", color: "text-rose-400", testid: "sidebar-tender-dashboard-link" });
    if (exec || ["production", "store"].includes(user.role)) list.push({ to: "/production-dashboard", icon: Factory, label: "Production Dashboard", color: "text-orange-400", testid: "sidebar-production-dashboard-link" });
    // Product Dashboard: anyone with products module access
    if ((modulesAccess || []).includes("products") || exec) list.push({ to: "/product-dashboard", icon: Package, label: "Product Dashboard", color: "text-slate-300", testid: "sidebar-product-dashboard-link" });
    return list;
  }, [user, isAdmin, modulesAccess]);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 inset-y-0 left-0 w-72 bg-slate-900 text-slate-300 flex flex-col transform ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 transition-transform duration-200`}
        data-testid="sidebar"
      >
        <div className="h-16 flex items-center gap-3 px-5 border-b border-slate-800">
          <img src={LOGO_URL} alt="HM" className="h-9 w-9 bg-white rounded p-1" />
          <div className="leading-tight">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">Hardik Meditech</div>
            <div className="text-sm font-bold text-white">OPS CONTROL</div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto scrollbar-thin px-3 py-4 space-y-6">
          <div>
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
                }`
              }
              data-testid="sidebar-dashboard-link"
            >
              <LayoutDashboard className="h-4 w-4" /> Dashboard
            </NavLink>
            {roleDashboards.map((rd) => (
              <NavLink
                key={rd.to}
                to={rd.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
                  }`
                }
                data-testid={rd.testid}
              >
                <rd.icon className={`h-4 w-4 ${rd.color}`} /> {rd.label}
              </NavLink>
            ))}
            {isAdmin && (
              <NavLink
                to="/ai-report"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
                  }`
                }
                data-testid="sidebar-ai-report-link"
              >
                <Sparkles className="h-4 w-4 text-amber-300" /> AI Weekly Report
              </NavLink>
            )}
            {isAdmin && (
              <NavLink
                to="/staff"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
                  }`
                }
                data-testid="sidebar-staff-link"
              >
                <Users className="h-4 w-4" /> Staff Directory
              </NavLink>
            )}
            {isAdmin && (
              <NavLink
                to="/notifications"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
                  }`
                }
                data-testid="sidebar-notifications-link"
              >
                <Radio className="h-4 w-4 text-emerald-400" /> Notifications
              </NavLink>
            )}
            <NavLink
              to="/field/expense"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
                }`
              }
              data-testid="sidebar-field-expense-link"
            >
              <Camera className="h-4 w-4 text-blue-300" /> Field Expense (Mobile)
            </NavLink>
            <NavLink
              to="/pbg-ledger"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
                }`
              }
              data-testid="sidebar-pbg-ledger-link"
            >
              <Landmark className="h-4 w-4 text-amber-300" /> PBG Ledger
            </NavLink>
            {isAdmin && (
              <NavLink
                to="/access-matrix"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
                  }`
                }
                data-testid="sidebar-access-matrix-link"
              >
                <Shield className="h-4 w-4 text-emerald-400" /> Access Matrix
              </NavLink>
            )}
            {isAdmin && (
              <NavLink
                to="/activity"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
                  }`
                }
                data-testid="sidebar-activity-link"
              >
                <Shield className="h-4 w-4" /> Activity Log
              </NavLink>
            )}
          </div>

          {Object.keys(grouped)
            .filter((g) => grouped[g].length > 0)
            .map((g) => (
              <div key={g}>
                <div className="px-3 mb-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                  {g}
                </div>
                <div className="space-y-1">
                  {grouped[g].map((m) => (
                    <NavLink
                      key={m.key}
                      to={`/m/${m.key}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                          isActive ? "bg-slate-800 text-white" : "text-slate-400 hover:bg-slate-800/60 hover:text-white"
                        }`
                      }
                      data-testid={`sidebar-link-${m.key}`}
                    >
                      <LucideIcon name={m.icon} className="h-4 w-4" />
                      <span className="truncate">{m.label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
        </nav>

        <div className="p-4 border-t border-slate-800 space-y-3">
          {/* Tier badge — makes the login hierarchy obvious at a glance */}
          {user?.role && (
            <div
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${TIER_STYLE[ROLE_TIER[user.role]] || TIER_STYLE[3]}`}
              data-testid="tier-badge"
            >
              <Shield className="h-3 w-3" />
              {TIER_LABEL[ROLE_TIER[user.role]] || "Level ?"}
            </div>
          )}
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-slate-700 text-white flex items-center justify-center text-xs font-bold">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
              <div className="text-[11px] text-slate-400 truncate">{ROLE_LABELS[user?.role] || user?.role}</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/50 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center px-4 sm:px-6 sticky top-0 z-20">
          <button
            className="lg:hidden p-2 -ml-2 rounded hover:bg-slate-100"
            onClick={() => setMobileOpen(true)}
            data-testid="menu-toggle"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="hidden md:flex items-center gap-2 ml-2">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-400 font-semibold">
              {location.pathname === "/" ? "Owner Dashboard" : "Operations"}
            </div>
          </div>

          <div className="flex-1" />

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              onClick={() => navigate("/")}
              data-testid="bell-alerts"
              title="Alerts"
            >
              <Bell className="h-5 w-5" />
              {alertsCount > 0 && (
                <span
                  className="absolute top-1 right-1 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
                  data-testid="alerts-badge"
                >
                  {alertsCount}
                </span>
              )}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-slate-100"
                  data-testid="user-menu-trigger"
                >
                  <div className="h-8 w-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold">
                    {initials}
                  </div>
                  <div className="hidden sm:block text-left leading-tight">
                    <div className="text-xs font-semibold text-slate-900">{user?.name}</div>
                    <div className="text-[10px] text-slate-500">{ROLE_LABELS[user?.role] || user?.role}</div>
                  </div>
                  <ChevronDown className="h-4 w-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-xs font-normal text-slate-500">Signed in as</div>
                  <div className="text-sm font-semibold">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-menu-item">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-x-hidden">{children}</main>
      </div>
    </div>
  );
}
